// Ask Ogma's confirm card (Craig, 30 Oct 2026; docs/specs/CAMPAIGN_LED_2026_10.md "Ask Ogma: actions"): what Ogma offers
// to do, done only when the person presses Do it, as them: a search, or a campaign paused or resumed. A campaign is
// drafted in the side window instead (SideWindow.tsx). Nothing is sent or approved from here.
// A search is longer work, so it runs as a task in the chat (Ask Ogma as your assistant, 30 Oct 2026). It ends with the
// firms found and two next steps: the best 20 beside the chat, or all of them on Contacts › Find. The planner's steps
// sit behind "How I searched" (Craig, 31 Oct 2026: too technical up front).
// Pressing Do it is the approval a search needs (2.19), as pressing Find firms is; nothing is spent (Ogma's database
// answers) and nobody is contacted until firms are in a running campaign.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ChatAction, ChatActionDone } from "../../lib/chat";
import { Link } from "react-router-dom";
import { decideCampaign } from "../../lib/campaigns";
import { addOwnerTask } from "../../lib/ownerTasks";
import { fmtNumber, plural } from "../../lib/format";
import { planProspects } from "../../lib/prospects";
import { useCrm, useOptionalAuth } from "../../lib/session";

const WHAT: Record<ChatAction["kind"], string> = {
  run_search: "Looks through Ogma's database of firms. Nothing is spent and nobody is contacted.",
  pause_campaign: "Pauses this campaign. Its work waits until you resume it; nothing is lost.",
  resume_campaign: "Resumes this campaign. Drafts still wait for your approval as set.",
  set_stage: "Moves the firm to this stage. Nothing is sent.",
  add_note: "Adds this note to the firm. Nothing is sent.",
  add_todo: "Adds this to your to-dos, on Conversations › Next steps.",
};

/** Where a search's firms open: Contacts › Find with the search (or, before it ran, its words) and the firms it covers. */
export const searchHref = (a: Extract<ChatAction, { kind: "run_search" }>, search?: string) =>
  `/contacts/find?${search ? `search=${encodeURIComponent(search)}` : `prospect=${encodeURIComponent(a.query)}`}${a.firms === "both" ? "" : `&firms=${a.firms}`}`;

/** onOpenPage: opens a page beside the chat (Chat's side window), so reviewing the firms found never leaves the chat. */
/** onAsk: asks Ogma for the next step in the chat ("Show the best 20 here"), so the search's firms open beside it. */
/** done: what Do it did, kept on the answer (a chat opened again shows it done, Craig 1 Nov 2026); onDone keeps it. */
export function ConfirmAction({ action, done = null, onDone, onOpenPage, onAsk }: { action: ChatAction; done?: ChatActionDone | null;
  onDone?: (done: ChatActionDone) => void; onOpenPage?: (path: string, title: string) => void; onAsk?: (text: string) => void }) {
  const crm = useCrm();
  const session = useOptionalAuth()?.session ?? null;
  const navigate = useNavigate();
  const [state, setState] = useState<"ask" | "busy" | "done" | "dismissed">(done ? "done" : "ask");
  const [note, setNote] = useState<string | null>(done && "note" in done ? done.note : null);
  // A search running as a task: its steps so far, then what it found
  const [steps, setSteps] = useState<string[]>([]);
  const [found, setFound] = useState<{ search: string; total: number } | null>(done && "search" in done ? done : null);
  const [showSteps, setShowSteps] = useState(false);
  if (state === "dismissed") return null;

  const doIt = async () => {
    setState("busy");
    setNote(null);
    try {
      if (action.kind === "run_search") {
        if (!session) {
          navigate(searchHref(action));
          return;
        }
        setSteps([]);
        const a = await planProspects({ token: session.access_token, query: action.query, onStep: (step) => setSteps((cur) => [...cur, step]) });
        // As Find firms does (31 Oct 2026): every search with firms shows its full list at once
        if (a.total > 0) await crm.decideProspectSearch(a.search_id, true);
        setFound({ search: a.search_id, total: a.total });
        setState("done");
        onDone?.({ search: a.search_id, total: a.total });
        return;
      }
      let said: string;
      if (action.kind === "set_stage") {
        await crm.setStage(action.organisation, action.stage, action.reason);
        said = "Moved.";
      } else if (action.kind === "add_note") {
        await crm.addNote(action.organisation, action.note);
        said = "Noted.";
      } else if (action.kind === "add_todo") {
        await addOwnerTask(crm, { title: action.todo, ...(action.due_on ? { due_on: action.due_on } : {}), ...(action.organisation ? { organisation_id: action.organisation } : {}) });
        said = "Added to your to-dos.";
      } else {
        await decideCampaign(crm, action.campaign, action.kind === "pause_campaign" ? "pause" : "resume");
        said = action.kind === "pause_campaign" ? "Paused." : "Resumed.";
      }
      setNote(said);
      setState("done");
      onDone?.({ note: said });
    } catch (e) {
      setNote((e as Error).message);
      setState("ask");
    }
  };

  return (
    <div className="chat-confirm" role="group" aria-label={action.title}>
      <strong>{action.title}</strong>
      <span className="muted small">{WHAT[action.kind]}</span>
      {state === "done" && found && action.kind === "run_search" ? (
        <>
          <span className="small" role="status">
            {found.total ? `Found ${fmtNumber(found.total)} ${plural(found.total, "firm")}.` : "No firms matched. Try fewer must-haves or a wider area."}
          </span>
          {found.total > 0 && (
            <span className="chat-confirm-buttons">
              {onAsk && (
                <button type="button" className="btn btn-small btn-primary" onClick={() => onAsk(`Show me the best ${Math.min(20, found.total)} firms from this search here`)}>
                  Show the best {Math.min(20, found.total)} here
                </button>
              )}
              <Link to={searchHref(action, found.search)} className="btn btn-small btn-quiet"
                onClick={(e) => {
                  if (!onOpenPage || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  onOpenPage(searchHref(action, found.search), "The firms found");
                }}>Review all {fmtNumber(found.total)}</Link>
            </span>
          )}
          {steps.length > 0 && (
            <button type="button" className="link-btn small" aria-expanded={showSteps} onClick={() => setShowSteps((v) => !v)}>
              {showSteps ? "Hide how I searched" : "How I searched"}
            </button>
          )}
          {showSteps && <ol className="plan-steps small muted" aria-label="How I searched">{steps.map((step, i) => <li key={i}>{step}</li>)}</ol>}
        </>
      ) : state === "done" ? (
        <span className="small" role="status">{note}</span>
      ) : (
        <span className="chat-confirm-buttons">
          <button type="button" className="btn btn-small btn-primary" disabled={state === "busy"} onClick={() => void doIt()}>
            {state === "busy" ? (action.kind === "run_search" ? "Searching…" : "Working…") : "Do it"}
          </button>
          <button type="button" className="btn btn-small btn-quiet" disabled={state === "busy"} onClick={() => setState("dismissed")}>
            Not now
          </button>
          {note && <span className="small" role="alert">{note}</span>}
        </span>
      )}
    </div>
  );
}
